import { DOCUMENT } from '@angular/common'
import { inject } from '@angular/core'
import type { HttpInterceptorFn } from '@angular/common/http'
import { Router } from '@angular/router'
import { catchError, throwError } from 'rxjs'
import { AuthService } from './auth.service'
import { SUPPRESS_LOGIN_REDIRECT } from './auth-context'

const isApi = (url: string) =>
  url.startsWith('/api') || url.includes('/api/')

/** The in-app URL the visitor is on or going to (see the 401 bounce below). */
function requestedPage(router: Router, doc: Document): string {
  const nav = router.currentNavigation()
  if (nav) return router.serializeUrl(nav.finalUrl ?? nav.extractedUrl)
  if (router.navigated) return router.url
  const loc = doc.location
  return loc ? `${loc.pathname}${loc.search}${loc.hash}` : router.url
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService)
  const router = inject(Router)
  const doc = inject(DOCUMENT)
  const cloned = isApi(req.url) ? req.clone({ withCredentials: true }) : req

  return next(cloned).pipe(
    catchError((err) => {
      if (err?.status === 401 && isApi(req.url) && !req.context.get(SUPPRESS_LOGIN_REDIRECT)) {
        // FR-050 AC-6.7: session expiry returns to the login of the brand shown —
        // the branded address `/a/<name>`, or `/login` in standard mode. Read BEFORE
        // clear(): the brand shown is still the signed-in user's.
        // FR-052 V9-1 (AC-7.2): on an agency host the login carries the page being
        // opened (a navigation in flight), the page shown, or — before the router's
        // first navigation (the app initializer's /me on a hard load) — the address bar.
        const target = auth.loginUrlFor(requestedPage(router, doc))
        auth.clear()
        void router.navigateByUrl(target)
      }
      return throwError(() => err)
    })
  )
}
